/**
 * Converts a string to proper case (title case) with intelligent handling
 * of various separators and edge cases.
 */
export function toProperCase(str?: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  // List of words that should remain lowercase (articles, prepositions, etc.)
  const lowercaseWords = new Set([
    'a',
    'an',
    'and',
    'as',
    'at',
    'but',
    'by',
    'for',
    'if',
    'in',
    'nor',
    'of',
    'on',
    'or',
    'so',
    'the',
    'to',
    'up',
    'yet',
    'vs',
    'via',
  ]);

  // List of words that should be all uppercase
  const uppercaseWords = new Set([
    'id',
    'api',
    'url',
    'uri',
    'html',
    'css',
    'js',
    'json',
    'xml',
    'sql',
    'ui',
    'ux',
    'ios',
    'usa',
    'uk',
    'eu',
    'faq',
    'seo',
    'ceo',
    'cto',
    'llc',
    'inc',
    'corp',
    'ltd',
  ]);

  // Roman numerals pattern
  const romanNumeralPattern =
    /^(i{1,3}|iv|v|vi{0,3}|ix|x{1,3}|xl|l|lx{0,3}|xc|c{1,3}|cd|d|dc{0,3}|cm|m{1,3})$/i;

  return (
    str
      // Split on various separators while preserving them
      .split(/(\s+|[-_./\\|,;:!?()[\]{}'"<>+=@#$%^&*~`])/g)
      .map((part, index, array) => {
        // Skip empty strings and separators
        if (!part || /^\s*$/.test(part) || /^[-_./\\|,;:!?()[\]{}'"<>+=@#$%^&*~`]+$/.test(part)) {
          return part;
        }

        const lowerPart = part.toLowerCase();
        const isFirstOrLast = index === 0 || index === array.length - 1;

        // Always capitalize first and last words
        if (isFirstOrLast) {
          return capitalizeWord(part, uppercaseWords, romanNumeralPattern);
        }

        // Handle special uppercase words
        if (uppercaseWords.has(lowerPart)) {
          return part.toUpperCase();
        }

        // Handle Roman numerals
        if (romanNumeralPattern.test(part)) {
          return part.toUpperCase();
        }

        // Keep certain words lowercase (unless first/last)
        if (lowercaseWords.has(lowerPart)) {
          return lowerPart;
        }

        // Default proper case
        return capitalizeWord(part, uppercaseWords, romanNumeralPattern);
      })
      .join('')
  );
}

/**
 * Helper function to capitalize a single word
 */
function capitalizeWord(
  word: string,
  uppercaseWords: Set<string>,
  romanNumeralPattern: RegExp
): string {
  if (!word) return word;

  const lowerWord = word.toLowerCase();

  // Handle special uppercase words
  if (uppercaseWords.has(lowerWord)) {
    return word.toUpperCase();
  }

  // Handle Roman numerals
  if (romanNumeralPattern.test(word)) {
    return word.toUpperCase();
  }

  // Handle contractions and possessives
  if (word.includes("'")) {
    return word
      .split("'")
      .map((part, i) => (i === 0 ? capitalize(part) : part.toLowerCase()))
      .join("'");
  }

  // Default capitalization
  return capitalize(word);
}

/**
 * Simple capitalize helper
 */
function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}
